/**
 * Dashboard export: every tile on a tab, composed into one image.
 *
 * Explore's image export (`plot/render/export-compose.ts`) cannot be reused - it
 * rebuilds its legends from the single global `runtime` and `plotState`, and a
 * dashboard has many charts. This composes from what is on screen instead:
 * each tile's uPlot canvas, copied pixel for pixel, placed where the tile sits
 * in the grid, with its title and legend drawn around it. What you export is
 * what the dashboard looks like - including its **Chart titles** and **Tile
 * frames** settings (`ExportLook`): titles off drops the tile titles and the
 * title band above the grid, frames off drops every card and outline.
 *
 * A map tile is several layers rather than one canvas - the GL basemap, the
 * track, and the seamark overlay's raster tiles - so it is composited in DOM
 * order, and the attribution and colour legend Leaflet draws in DOM are
 * redrawn on top. Basemap and seamark tiles are fetched with CORS and the GL
 * canvas keeps its drawing buffer (`preserveDrawingBuffer`), so none of this
 * taints the export or copies back empty.
 *
 * **Every tile is loaded first.** Tiles fetch only once scrolled into view, so
 * an export would otherwise show "Waiting…" for everything below the fold.
 * `prepareForExport()` loads them now, through the same request queue, and the
 * caller gets progress while they arrive.
 *
 * Composed at the screen's pixel ratio, so the charts are copied 1:1 rather
 * than resampled. uPlot's pixel ratio is a global (`uPlot.pxRatio`); raising it
 * for an export would redraw Explore's chart too.
 *
 * PNG is one tab. PDF is every tab, a page each - the tabs are switched through
 * by the caller, since a tab's tiles only exist while it is the one on screen.
 */

import { showToast } from '../../ui/toast.ts';
import { getCssVar } from '../plot/render/chart-dom.ts';
import type { DashboardTileEl } from './dashboard-tile.ts';

/** Inset around the grid, and the height of the title band above it (css px). */
const PAD = 24;
const HEADER_H = 64;
/** Longest a tile may take to become drawable before the export goes without it. */
const SETTLE_TIMEOUT_MS = 90_000;

export interface ExportHeader {
  title: string;
  subtitle: string;
}

/** The dashboard settings an export follows, so the image matches the screen. */
export interface ExportLook {
  titles: boolean;
  frames: boolean;
}

export interface ExportProgress {
  done: number;
  total: number;
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Load every tile in `grid` and wait until none is still fetching. Reports
 * progress as tiles settle; gives up waiting after `SETTLE_TIMEOUT_MS`, so one
 * stuck request costs a blank tile rather than the whole export.
 */
async function settleTiles(
  tiles: DashboardTileEl[],
  onProgress: (p: ExportProgress) => void,
): Promise<void> {
  for (const tile of tiles) tile.prepareForExport();
  const started = Date.now();
  for (;;) {
    const done = tiles.filter((t) => !t.exportBusy).length;
    onProgress({ done, total: tiles.length });
    if (done === tiles.length || Date.now() - started > SETTLE_TIMEOUT_MS) return;
    await sleep(150);
  }
}

/** Cut `text` to `maxW` with an ellipsis, using the context's current font. */
function fitText(ctx: CanvasRenderingContext2D, text: string, maxW: number): string {
  if (ctx.measureText(text).width <= maxW) return text;
  let lo = 0;
  let hi = text.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (ctx.measureText(text.slice(0, mid) + '…').width <= maxW) lo = mid;
    else hi = mid - 1;
  }
  return text.slice(0, lo) + '…';
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') ctx.roundRect(x, y, w, h, r);
  else ctx.rect(x, y, w, h);
}

/** A DOM rect relative to `origin`, in css px. */
function rel(el: Element, origin: DOMRect): { x: number; y: number; w: number; h: number } {
  const r = el.getBoundingClientRect();
  return { x: r.left - origin.left, y: r.top - origin.top, w: r.width, h: r.height };
}

const FONT = 'system-ui, -apple-system, "Segoe UI", sans-serif';

/**
 * Compose the tab currently mounted in `grid` into one canvas: a title band,
 * then every tile where it sits.
 */
export async function composeGrid(
  grid: HTMLElement,
  header: ExportHeader,
  onProgress: (p: ExportProgress) => void,
  look: ExportLook = { titles: true, frames: true },
): Promise<HTMLCanvasElement> {
  const tiles = [...grid.querySelectorAll<DashboardTileEl>('dashboard-tile')];
  await settleTiles(tiles, onProgress);
  // One more frame, so the last tile to arrive has drawn and fitted its legend.
  await new Promise((resolve) => requestAnimationFrame(() => resolve(null)));

  const canvasEl = grid.querySelector<HTMLElement>('.dash-grid-canvas') ?? grid;
  const origin = canvasEl.getBoundingClientRect();
  const contentH = tiles.reduce((m, t) => {
    const r = rel(t, origin);
    return Math.max(m, r.y + r.h);
  }, 0);

  const S = window.devicePixelRatio || 1;
  // Titles off: no band above the grid either - just the charts.
  const headerH = look.titles ? HEADER_H : PAD;
  const width = origin.width + PAD * 2;
  const height = headerH + Math.max(contentH, 80) + PAD;
  const out = document.createElement('canvas');
  out.width = Math.round(width * S);
  out.height = Math.round(height * S);
  const ctx = out.getContext('2d');
  if (!ctx) throw new Error('Canvas is not available');
  ctx.scale(S, S);

  const color = {
    bg: getCssVar('--bg') || '#ffffff',
    surface: getCssVar('--surface') || '#ffffff',
    border: getCssVar('--border') || '#dddddd',
    text: getCssVar('--text') || '#111111',
    dim: getCssVar('--text-dim') || '#555555',
    faint: getCssVar('--text-faint') || '#888888',
    danger: getCssVar('--danger') || '#c0392b',
  };

  ctx.fillStyle = color.bg;
  ctx.fillRect(0, 0, width, height);

  ctx.textBaseline = 'alphabetic';
  if (look.titles) {
    ctx.fillStyle = color.text;
    ctx.font = `600 18px ${FONT}`;
    ctx.fillText(fitText(ctx, header.title, width - PAD * 2), PAD, PAD + 16);
    ctx.fillStyle = color.dim;
    ctx.font = `400 12px ${FONT}`;
    ctx.fillText(fitText(ctx, header.subtitle, width - PAD * 2), PAD, PAD + 36);
  }

  if (!tiles.length) {
    ctx.fillStyle = color.faint;
    ctx.font = `400 13px ${FONT}`;
    ctx.fillText('No charts on this tab.', PAD, headerH + 30);
    return out;
  }

  ctx.save();
  ctx.translate(PAD, headerH);
  for (const tile of tiles) drawTile(ctx, tile, origin, color, look);
  ctx.restore();
  return out;
}

function drawTile(
  ctx: CanvasRenderingContext2D,
  tile: DashboardTileEl,
  origin: DOMRect,
  color: Record<'bg' | 'surface' | 'border' | 'text' | 'dim' | 'faint' | 'danger', string>,
  look: ExportLook,
): void {
  const box = rel(tile, origin);

  // Card - unless the dashboard has frames off, when charts sit on the page.
  if (look.frames) {
    roundRect(ctx, box.x + 0.5, box.y + 0.5, box.w - 1, box.h - 1, 8);
    ctx.fillStyle = color.surface;
    ctx.fill();
    ctx.strokeStyle = color.border;
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  // Header: the title, and a rule under it when there is a card to rule.
  // With titles off the chart already fills the tile on screen, so there is
  // nothing to leave room for.
  const head = tile.querySelector('.dash-tile-head');
  const headH = look.titles && head ? head.getBoundingClientRect().height : 0;
  if (look.titles) {
    const title = tile.querySelector('.dash-tile-title')?.textContent?.trim() ?? '';
    ctx.fillStyle = color.text;
    ctx.font = `500 13px ${FONT}`;
    ctx.textBaseline = 'middle';
    ctx.fillText(fitText(ctx, title, box.w - 20), box.x + 10, box.y + headH / 2);
    ctx.textBaseline = 'alphabetic';
    if (look.frames) {
      ctx.beginPath();
      ctx.moveTo(box.x + 1, box.y + headH + 0.5);
      ctx.lineTo(box.x + box.w - 1, box.y + headH + 0.5);
      ctx.strokeStyle = color.border;
      ctx.stroke();
    }
  }

  // The chart, copied as drawn.
  const state = tile.querySelector('.dash-tile-state');
  const mapEl = tile.querySelector<HTMLElement>('.dash-tile-chart .leaflet-map');
  if (mapEl && !state) {
    drawMapTile(ctx, mapEl, origin, color);
    return;
  }
  const chart = tile.querySelector<HTMLCanvasElement>('.dash-tile-chart canvas');
  if (chart && !state) {
    const r = rel(chart, origin);
    ctx.drawImage(chart, r.x, r.y, r.w, r.h);
    drawLegend(ctx, tile, origin, color);
    return;
  }

  // Not a chart: whatever the tile says instead - no ship, an error, a
  // catalog chart that has gone. Better on the page than silently blank.
  const message = state?.querySelector('span:not(.spinner)')?.textContent?.trim()
    || 'Not loaded';
  const isError = !!state?.classList.contains('is-error');
  ctx.fillStyle = isError ? color.danger : color.faint;
  ctx.font = `400 12px ${FONT}`;
  ctx.textAlign = 'center';
  ctx.fillText(
    fitText(ctx, message, box.w - 24),
    box.x + box.w / 2,
    box.y + headH + (box.h - headH) / 2,
  );
  ctx.textAlign = 'left';
}

/**
 * A map tile: every layer it is made of, then what Leaflet draws in DOM.
 *
 * Layers are copied in **pane z-index order**, not document order: Leaflet's
 * panes are stacked by z-index, and the basemap's own pane is created last and
 * pushed underneath with a low one. Copying in document order would paint the
 * basemap over the track it belongs under. Tiles still loading are skipped: a
 * raster tile fades in, and copying a half-faded one would bake the fade into
 * the export.
 */
/** The z-index of the Leaflet pane `el` sits in - what decides paint order. */
function paneZ(el: Element, mapEl: HTMLElement): number {
  for (let node: Element | null = el; node && node !== mapEl; node = node.parentElement) {
    if (!node.classList.contains('leaflet-pane')) continue;
    const z = Number.parseInt(getComputedStyle(node).zIndex, 10);
    if (Number.isFinite(z)) return z;
  }
  return 0;
}

function drawMapTile(
  ctx: CanvasRenderingContext2D,
  mapEl: HTMLElement,
  origin: DOMRect,
  color: Record<string, string>,
): void {
  const box = rel(mapEl, origin);
  ctx.save();
  ctx.beginPath();
  ctx.rect(box.x, box.y, box.w, box.h);
  ctx.clip();

  const layers = [
    ...mapEl.querySelectorAll<HTMLCanvasElement | HTMLImageElement>('canvas, img.leaflet-tile'),
  ]
    .map((el, i) => ({ el, z: paneZ(el, mapEl), i }))
    // Ties keep document order, which is what stacks two layers in one pane.
    .sort((a, b) => a.z - b.z || a.i - b.i)
    .map((entry) => entry.el);
  layers.forEach((layer) => {
    if (layer instanceof HTMLImageElement && (!layer.complete || !layer.naturalWidth)) return;
    const r = rel(layer, origin);
    if (r.w < 1 || r.h < 1) return;
    try {
      ctx.drawImage(layer, r.x, r.y, r.w, r.h);
    } catch {
      // One unreadable layer (a tile that failed CORS) must not lose the map.
    }
  });

  // The on-map colour legend, redrawn as a box rather than copied: it is DOM.
  const legend = mapEl.querySelector<HTMLElement>('.map-legend');
  if (legend) {
    const l = rel(legend, origin);
    roundRect(ctx, l.x, l.y, l.w, l.h, 4);
    ctx.fillStyle = color.surface ?? '#fff';
    ctx.fill();
    ctx.strokeStyle = color.border ?? '#ccc';
    ctx.stroke();
    const title = legend.querySelector<HTMLElement>('.map-legend-title');
    if (title?.textContent) {
      const t = rel(title, origin);
      ctx.fillStyle = color.text ?? '#111';
      ctx.font = `600 11px ${FONT}`;
      ctx.textBaseline = 'middle';
      ctx.fillText(title.textContent.trim(), t.x, t.y + t.h / 2);
    }
    legend.querySelectorAll<HTMLElement>('.map-legend-row').forEach((row) => {
      const swatch = row.querySelector<HTMLElement>('.map-legend-swatch');
      if (!swatch) return;
      const sw = rel(swatch, origin);
      const rr = rel(row, origin);
      ctx.fillStyle = getComputedStyle(swatch).backgroundColor;
      ctx.fillRect(sw.x, sw.y, sw.w, sw.h);
      ctx.fillStyle = color.text ?? '#111';
      ctx.font = `500 11px ${FONT}`;
      ctx.fillText(row.textContent?.trim() ?? '', sw.x + sw.w + 5, rr.y + rr.h / 2);
    });
    ctx.textBaseline = 'alphabetic';
  }

  // Attribution: required by the tile sources, and it is DOM too.
  const attribution = mapEl.querySelector<HTMLElement>('.leaflet-control-attribution');
  const credit = attribution?.textContent?.trim();
  if (credit) {
    ctx.font = `400 9px ${FONT}`;
    ctx.fillStyle = color.faint ?? '#888';
    ctx.textAlign = 'right';
    ctx.fillText(fitText(ctx, credit, box.w - 8), box.x + box.w - 4, box.y + box.h - 4);
    ctx.textAlign = 'left';
  }
  ctx.restore();
}

/**
 * uPlot's legend is DOM, not canvas. Redraw its rows where they sit on screen:
 * a swatch in the marker's colour and the label, dimmed when the series is off.
 */
function drawLegend(
  ctx: CanvasRenderingContext2D,
  tile: DashboardTileEl,
  origin: DOMRect,
  color: Record<string, string>,
): void {
  const rows = tile.querySelectorAll<HTMLElement>('.u-legend .u-series');
  rows.forEach((row) => {
    const marker = row.querySelector<HTMLElement>('.u-marker');
    const label = row.querySelector<HTMLElement>('.u-label');
    if (!marker || !label?.textContent?.trim()) return;
    // uPlot keeps the x series' row in the DOM, hidden.
    if (!row.getClientRects().length) return;
    const off = row.classList.contains('u-off');
    const m = rel(marker, origin);
    const cs = getComputedStyle(marker);
    ctx.globalAlpha = off ? 0.35 : 1;
    const filled = cs.backgroundColor && cs.backgroundColor !== 'rgba(0, 0, 0, 0)';
    if (filled) {
      ctx.fillStyle = cs.backgroundColor;
      ctx.fillRect(m.x, m.y, m.w, m.h);
    } else {
      // Hollow, as on screen: the marker is its border.
      const bw = parseFloat(cs.borderTopWidth) || 2;
      ctx.strokeStyle = cs.borderTopColor;
      ctx.lineWidth = bw;
      ctx.strokeRect(m.x + bw / 2, m.y + bw / 2, m.w - bw, m.h - bw);
      ctx.lineWidth = 1;
    }
    const l = rel(label, origin);
    ctx.fillStyle = color.text ?? '#111';
    ctx.font = `600 12px ${FONT}`;
    ctx.textBaseline = 'middle';
    ctx.fillText(label.textContent.trim(), l.x, l.y + l.h / 2);
    ctx.textBaseline = 'alphabetic';
    ctx.globalAlpha = 1;
  });
}

/* ── Saving ─────────────────────────────────────────────── */

/** A filename-safe stem: the dashboard name and a timestamp. */
export function exportStem(name: string, suffix: string): string {
  const slug = name.trim().replace(/[^\w-]+/g, '-').replace(/^-+|-+$/g, '') || 'dashboard';
  const ts = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');
  return `${slug}${suffix ? `_${suffix}` : ''}_${ts}`;
}

export function savePng(canvas: HTMLCanvasElement, stem: string): void {
  const link = document.createElement('a');
  link.download = `${stem}.png`;
  link.href = canvas.toDataURL('image/png');
  link.click();
}

/** One page per canvas, each page the size of its canvas. jsPDF is lazy-loaded. */
export async function savePdf(pages: HTMLCanvasElement[], stem: string): Promise<void> {
  let jsPDF: typeof import('jspdf').jsPDF;
  try {
    ({ jsPDF } = await import('jspdf'));
  } catch {
    showToast('error', 'PDF library not loaded - check network access');
    return;
  }
  const first = pages[0];
  if (!first) return;
  const orient = (c: HTMLCanvasElement) => (c.width >= c.height ? 'landscape' : 'portrait');
  const pdf = new jsPDF({
    orientation: orient(first),
    unit: 'px',
    format: [first.width, first.height],
    hotfixes: ['px_scaling'],
  });
  pages.forEach((page, i) => {
    if (i > 0) pdf.addPage([page.width, page.height], orient(page));
    pdf.addImage(page.toDataURL('image/png'), 'PNG', 0, 0, page.width, page.height, undefined, 'FAST');
  });
  pdf.save(`${stem}.pdf`);
}
